/**
 * Utilities for music notation, key signatures, and accidentals
 *
 * These utilities help convert MIDI notes (absolute pitch) to properly
 * notated music considering key signatures and enharmonic spellings.
 */

import { NoteName, NoteWithAccidental } from "./types";

/**
 * Key signature alterations
 * Maps which notes are sharp or flat in each key signature
 */
const KEY_SIGNATURE_ALTERATIONS: Record<string, Map<string, "sharp" | "flat">> = {};

// Sharp keys
const SHARP_KEYS: Record<string, string[]> = {
    "G": ["F"],
    "D": ["F", "C"],
    "A": ["F", "C", "G"],
    "E": ["F", "C", "G", "D"],
    "B": ["F", "C", "G", "D", "A"],
    "F#": ["F", "C", "G", "D", "A", "E"],
    "C#": ["F", "C", "G", "D", "A", "E", "B"],
};

// Flat keys
const FLAT_KEYS: Record<string, string[]> = {
    "F": ["B"],
    "Bb": ["B", "E"],
    "Eb": ["B", "E", "A"],
    "Ab": ["B", "E", "A", "D"],
    "Db": ["B", "E", "A", "D", "G"],
    "Gb": ["B", "E", "A", "D", "G", "C"],
    "Cb": ["B", "E", "A", "D", "G", "C", "F"],
};

// Build the alteration maps
Object.entries(SHARP_KEYS).forEach(([key, notes]) => {
    const map = new Map<string, "sharp" | "flat">();
    notes.forEach(note => map.set(note, "sharp"));
    KEY_SIGNATURE_ALTERATIONS[key] = map;
});

Object.entries(FLAT_KEYS).forEach(([key, notes]) => {
    const map = new Map<string, "sharp" | "flat">();
    notes.forEach(note => map.set(note, "flat"));
    KEY_SIGNATURE_ALTERATIONS[key] = map;
});

/**
 * Get the alterations for a given key signature
 * @param keySignature Key signature (e.g., "C", "G", "Bb", "F#")
 * @returns Map of note letter to alteration type
 */
export function getKeySignatureAlterations(keySignature?: string): Map<string, "sharp" | "flat"> {
    if (!keySignature || keySignature === "C") {
        return new Map();
    }
    return KEY_SIGNATURE_ALTERATIONS[keySignature] || new Map();
}

/**
 * Determine if a key signature uses flats (vs sharps)
 * @param keySignature Key signature string
 * @returns true if the key signature uses flats
 */
export function shouldPreferFlat(keySignature?: string): boolean {
    if (!keySignature) return false;
    return keySignature in FLAT_KEYS;
}

/**
 * Convert MIDI note to note name with preferred spelling
 * @param midi MIDI note number
 * @param preferFlat Prefer flat spelling for enharmonic notes
 * @returns Note name (e.g., "C4", "F#5", "Bb4")
 */
export function midiToNoteName(midi: number, preferFlat: boolean = false): NoteName {
    const octave = Math.floor(midi / 12) - 1;
    const pitchClass = midi % 12;

    const sharpNames: NoteWithAccidental[] = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
    const flatNames : NoteWithAccidental[] = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"];

    const noteName = preferFlat ? flatNames[pitchClass] : sharpNames[pitchClass];
    return `${noteName}${octave}`;
}

/**
 * Determine what accidental should be shown for a note in a key signature
 *
 * This is the core logic that ensures MIDI notes (absolute pitch) are correctly
 * notated with appropriate accidentals based on the key signature context.
 *
 * @param midi MIDI note number (absolute pitch)
 * @param noteName The note name that will be rendered (e.g., "E5", "F#5")
 * @param keySignature Current key signature
 * @returns Accidental symbol to show, or null if none needed
 *   - "n" = natural
 *   - "#" = sharp
 *   - "b" = flat
 *   - null = no accidental (matches key signature)
 *
 * @example
 * // E-natural (MIDI 76) in Bb key (which has Eb)
 * getRequiredAccidental(76, "E5", "Bb") // returns "n" (show natural)
 *
 * @example
 * // Eb (MIDI 75) in Bb key
 * getRequiredAccidental(75, "Eb5", "Bb") // returns null (matches key sig)
 *
 * @example
 * // F# (MIDI 78) in C key
 * getRequiredAccidental(78, "F#5", "C") // returns "#" (not in key sig)
 */
export function getRequiredAccidental(
    midi: number,
    noteName: string,
    keySignature?: string
): string | null {
    const alterations = getKeySignatureAlterations(keySignature);

    // Extract note letter (C, D, E, etc.) from note name like "C5", "F#5", "Bb4"
    const match = noteName.match(/^([A-G])([#b]?)(\d+)$/);
    if (!match) return null;

    const [, letter, explicitAccidental] = match;
    const keyAlteration = alterations.get(letter);

    // If the key signature has an alteration for this note letter
    if (keyAlteration) {
        // If the note matches the key signature alteration, no accidental needed
        if (explicitAccidental === "#" && keyAlteration === "sharp") return null;
        if (explicitAccidental === "b" && keyAlteration === "flat") return null;

        // If the note is natural but key sig says it should be altered, show natural
        if (explicitAccidental === "" && keyAlteration) return "n";

        // If the note has opposite alteration from key sig, show it explicitly
        if (explicitAccidental === "#" && keyAlteration === "flat") return "#";
        if (explicitAccidental === "b" && keyAlteration === "sharp") return "b";
    } else {
        // Key signature doesn't alter this note - it should be natural
        // If there's an accidental, we need to show it explicitly
        if (explicitAccidental === "#") return "#";
        if (explicitAccidental === "b") return "b";
    }

    return null;
}

/**
 * Check if a key signature is valid
 * @param keySignature Key signature string
 * @returns true if valid
 */
export function isValidKeySignature(keySignature: string): boolean {
    return keySignature === "C" || keySignature in KEY_SIGNATURE_ALTERATIONS;
}

/**
 * Get all supported key signatures
 * @returns Array of key signature strings
 */
export function getSupportedKeySignatures(): string[] {
    return ["C", ...Object.keys(KEY_SIGNATURE_ALTERATIONS)];
}
